import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { NAV } from "../data/site";
import { useShop } from "../context/ShopContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { count } = useShop();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "bg-[#f4eee2]/90 backdrop-blur-xl border-b border-[#d9cdba]"
            : "bg-[#f4eee2]/70 backdrop-blur-md border-b border-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/emblem-dark.png" alt="The Scent Butler emblem" className="w-10 h-10 object-contain" />
            <div className="leading-none">
              <span className="font-serif text-base md:text-lg tracking-wide text-[#201a12]">THE SCENT BUTLER</span>
              <span className="block eyebrow mt-1 text-[0.55rem]">Commercial Scenting</span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${slug(n.label)}`}
                className={`text-sm font-medium tracking-wide link-underline transition-colors duration-300 ${
                  pathname === n.to ? "text-gold" : "text-[#201a12] hover:text-black"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1 md:gap-2">
            <Link to="/account" data-testid="nav-account" aria-label="Account"
              className="p-2.5 text-[#201a12] hover:text-gold transition-colors">
              <User size={19} strokeWidth={1.6} />
            </Link>
            <Link to="/cart" data-testid="nav-cart" aria-label="Bag"
              className="relative p-2.5 text-[#201a12] hover:text-gold transition-colors">
              <ShoppingBag size={19} strokeWidth={1.6} />
              {count > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center bg-[#221d17] text-[#f4eee2] text-[0.6rem] font-semibold rounded-full"
                  data-testid="nav-cart-count">
                  {count}
                </span>
              )}
            </Link>
            <Link
              to="/enquiries"
              data-testid="nav-cta"
              className="hidden lg:inline-flex items-center ml-2 px-6 py-3 text-xs font-semibold tracking-[0.2em] uppercase transition-colors duration-300 bg-[#221d17] text-[#f4eee2] hover:bg-black"
            >
              Book Consultation
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 text-[#201a12]"
              data-testid="nav-mobile-toggle"
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#f4eee2]/98 backdrop-blur-xl md:hidden pt-24 px-8 overflow-y-auto"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1 pb-16">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={n.to} className="font-serif text-4xl py-3 block text-[#201a12]" data-testid={`mobile-nav-${slug(n.label)}`}>
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/account" className="font-serif text-4xl py-3 block text-[#201a12]" data-testid="mobile-nav-account">Account</Link>
              <Link
                to="/enquiries"
                className="mt-6 inline-flex items-center justify-center px-6 py-4 text-xs font-semibold tracking-[0.2em] uppercase text-[#f4eee2] bg-[#221d17]"
              >
                Book Consultation
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
